import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Project, ProjectSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaLink } from '@/components/generals/cta-link'
import { HeroTitle } from '@/components/generals/hero-title'
import { PlaceholderTag } from '@/components/generals/placeholder-tag'
import { SectionLabel } from '@/components/generals/section-label'
import ProjectGrid from '@/components/sections/project-grid'
import FinalCta from '@/components/sections/final-cta'

import {
    REVALIDATE_SECONDS,
    getProject,
    getProjects,
    getSlugs
} from '@/lib/content'

interface CaseStudyPageProps {
    project: Project
    number: number
    total: number
    related: ProjectSummary[]
}

const CaseStudyPage: FC<CaseStudyPageProps> = ({ project, number = 1, total = 1, related }): ReactNode => {
    const gallery = project.gallery.filter((image) => image.src)

    const chapters = [
        { label: 'The business challenge', body: project.challenge },
        { label: 'Strategic approach', body: project.approach }
    ].filter((chapter) => chapter.body)

    return (
        <Layout>
            <Seo title={project.name} description={project.summary || project.tagline} ogImage={project.cover ?? undefined} />

            <section id='case-hero' aria-labelledby='case-title' data-nav-theme='light' className='relative overflow-hidden bg-cream text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 pb-14 pt-32 md:px-10 md:pb-16 md:pt-40'>
                    <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-4 pb-5'>
                        <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-charcoal/20' style={revealDelay(100)} />
                        <Link href='/work' className='reveal-fade group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/80 transition-colors hover:text-charcoal'>
                            <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-1' />
                            All work
                        </Link>

                        <div className='reveal-fade flex flex-wrap items-center gap-2' style={revealDelay(150)}>
                            {project.category && (
                                <span className='rounded-sm bg-charcoal px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-cream'>{project.category}</span>
                            )}
                            {project.isConcept && <PlaceholderTag>Concept project</PlaceholderTag>}
                        </div>
                    </div>

                    <div className='mt-10 grid gap-10 md:mt-14 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-8'>
                            <p className='reveal-fade font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal/80' style={revealDelay(180)}>
                                Case study {pad(number)} / {pad(total)}
                            </p>
                            <HeroTitle
                                id='case-title'
                                className='mt-4 font-wide text-[clamp(3.25rem,8vw,7.5rem)] font-black uppercase leading-[0.85] tracking-[-0.05em]'
                                delay={240}
                                lines={[
                                    <>
                                        {project.name}
                                        <span aria-hidden='true' className='ml-[0.06em] inline-block size-[0.14em] ring-1 ring-charcoal/20' style={{ backgroundColor: project.accent }} />
                                    </>
                                ]}
                            />
                            <p className='reveal-fade mt-6 max-w-2xl font-heading text-[clamp(1.3rem,2vw,1.75rem)] font-bold leading-[1.22] tracking-[-0.025em] md:mt-8' style={revealDelay(400)}>
                                {project.tagline}
                            </p>
                        </div>

                        <dl className='grid grid-cols-2 gap-x-6 gap-y-6 lg:col-span-4'>
                            {[
                                { term: 'Client', value: project.name },
                                { term: 'Industry', value: project.industry },
                                { term: 'Year', value: project.year },
                                { term: 'Type', value: project.isConcept ? 'Concept' : 'Client project' }
                            ].filter((item) => item.value).map((item, index) => (
                                <div key={item.term} className='reveal-fade border-t border-charcoal pt-3' style={revealDelay(460 + index * 60)}>
                                    <dt className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>{item.term}</dt>
                                    <dd className='mt-1.5 text-[15px] font-semibold'>{item.value}</dd>
                                </div>
                            ))}
                            {project.services.length > 0 && (
                                <div className='reveal-fade col-span-2 border-t border-charcoal pt-3' style={revealDelay(720)}>
                                    <dt className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>Services</dt>
                                    <dd className='mt-2 flex flex-wrap gap-2'>
                                        {project.services.map((service) => (
                                            <Link
                                                key={service.slug}
                                                href={`/services/${service.slug}`}
                                                className='rounded-sm border border-charcoal/30 px-2.5 py-1 text-[13px] transition-colors duration-300 hover:border-charcoal hover:bg-charcoal hover:text-cream'
                                            >
                                                {service.title}
                                            </Link>
                                        ))}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>

                {project.cover && (
                    <div className='mx-auto max-w-[1440px] px-5 pb-20 md:px-10 md:pb-24'>
                        <figure className='relative'>
                            <div aria-hidden='true' className='reveal-clip absolute -bottom-5 right-[8%] h-1/2 w-1/3 md:-bottom-7' style={{ ...revealDelay(420), backgroundColor: project.accent }} />
                            <div className='reveal-clip relative aspect-[4/3] overflow-hidden bg-stone sm:aspect-[16/9] lg:aspect-[21/9]' style={revealDelay(480)}>
                                <Image src={project.cover} alt={project.coverAlt} fill priority sizes='100vw' className='object-cover' />
                                <span className='absolute bottom-4 left-4 bg-cream px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal'>
                                    {project.name} / Key visual
                                </span>
                            </div>
                        </figure>
                    </div>
                )}
            </section>

            {project.overview && (
                <section aria-labelledby='overview-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto grid max-w-[1440px] gap-8 border-t border-charcoal/20 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12'>
                        <div className='lg:col-span-4'>
                            <SectionLabel>Project overview</SectionLabel>
                            <h2 id='overview-title' className='sr-only'>Project overview</h2>
                        </div>
                        <p data-reveal className='font-heading text-[clamp(1.4rem,2.3vw,2.15rem)] font-semibold leading-[1.26] tracking-[-0.025em] lg:col-span-8'>
                            {project.overview}
                        </p>
                    </div>
                </section>
            )}

            {chapters.length > 0 && (
                <section aria-label='Challenge and approach' data-nav-theme='dark' className='bg-charcoal text-cream'>
                    <div className='mx-auto grid max-w-[1440px] gap-16 px-5 py-20 md:grid-cols-2 md:gap-12 md:px-10 md:py-28'>
                        {chapters.map((chapter, index) => (
                            <article key={chapter.label} data-reveal className={cn(index === 1 && 'md:mt-24')}>
                                <span className='font-heading font-wide text-6xl font-black leading-none tracking-[-0.04em] text-coral md:text-7xl'>
                                    {pad(index + 1)}
                                </span>
                                <h2 className='mt-8 font-heading font-wide text-2xl font-black uppercase tracking-[-0.015em] md:text-3xl'>
                                    {chapter.label}
                                </h2>
                                <p className='mt-5 max-w-xl text-[17px] leading-relaxed text-cream/80'>{chapter.body}</p>
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {project.concept && (
                <section aria-labelledby='concept-title' data-nav-theme='light' className='relative overflow-hidden text-charcoal' style={{ backgroundColor: project.accent }}>
                    <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:items-center'>
                        <div data-reveal className='lg:col-span-7'>
                            <SectionLabel className='text-charcoal'>Creative concept</SectionLabel>
                            <h2 id='concept-title' className='sr-only'>Creative concept</h2>
                            <p className='mt-8 font-heading text-[clamp(1.5rem,2.6vw,2.4rem)] font-bold leading-[1.2] tracking-[-0.03em]'>
                                {project.concept}
                            </p>
                        </div>
                        {project.detailImage && (
                            <div className='lg:col-span-5'>
                                <div data-reveal-image className='relative aspect-[4/5] overflow-hidden bg-charcoal/10'>
                                    <Image src={project.detailImage} alt={project.detailImageAlt} fill sizes='(min-width: 1024px) 40vw, 100vw' className='object-cover' />
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {project.execution.length > 0 && (
                <section aria-labelledby='execution-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <div data-reveal className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                            <div>
                                <SectionLabel>Campaign execution</SectionLabel>
                                <h2 id='execution-title' className='mt-6 font-heading type-section'>
                                    How it comes to life.
                                </h2>
                            </div>
                        </div>

                        <ol className='mt-14 grid border-l border-t border-charcoal/25 sm:grid-cols-2 lg:grid-cols-4'>
                            {project.execution.map((item, index) => (
                                <li key={item} data-reveal className='flex min-h-48 flex-col justify-between gap-8 border-b border-r border-charcoal/25 p-6 md:p-8'>
                                    <span className='font-mono text-[11px] text-charcoal/75'>/ {pad(index + 1)}</span>
                                    <span className='font-heading text-lg font-bold leading-snug tracking-[-0.015em] md:text-xl'>{item}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            )}

            {gallery.length > 0 && (
                <section aria-label='Campaign imagery' data-nav-theme='dark' className='bg-charcoal text-cream'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <SectionLabel tone='dark'>Campaign imagery</SectionLabel>
                        <div className='mt-12 grid gap-6 md:grid-cols-12 md:gap-8'>
                            {gallery.slice(0, 6).map((image, index) => (
                                <figure
                                    key={`${image.src}-${index}`}
                                    data-reveal
                                    className={cn(
                                        index % 3 === 0 && 'md:col-span-7',
                                        index % 3 === 1 && 'md:col-span-5 md:mt-28',
                                        index % 3 === 2 && 'md:col-span-6 md:col-start-4'
                                    )}
                                >
                                    <div data-reveal-image className={cn('relative overflow-hidden bg-charcoal-soft', index % 3 === 1 ? 'aspect-[4/5]' : 'aspect-[4/3]')}>
                                        <Image src={image.src} alt={image.alt} fill sizes='(min-width: 768px) 55vw, 100vw' className='object-cover' />
                                    </div>
                                    <figcaption className='mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60'>
                                        {project.name} / {pad(index + 1)}
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {project.results.length > 0 && (
                <section aria-labelledby='results-title' data-nav-theme='light' className='bg-stone text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <div data-reveal className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                            <div>
                                <SectionLabel>{project.isConcept ? 'Target outcomes' : 'Results'}</SectionLabel>
                                <h2 id='results-title' className='mt-6 font-heading type-section'>
                                    {project.isConcept ? 'What success looks like.' : 'Results and metrics.'}
                                </h2>
                            </div>
                            {project.isConcept && (
                                <p className='flex max-w-sm flex-wrap items-center gap-3 text-[14px] text-charcoal/85'>
                                    <PlaceholderTag>Illustrative</PlaceholderTag>
                                    Targets set for a concept project, not results from a real client.
                                </p>
                            )}
                        </div>

                        <ul className='mt-14 grid border-t border-charcoal md:grid-cols-3'>
                            {project.results.map((result, index) => (
                                <li key={`${result.value}-${index}`} data-reveal className='border-b border-charcoal/30 py-10 md:border-b-0 md:border-l md:px-8 md:first:border-l-0 md:first:pl-0'>
                                    <p className='font-heading font-wide text-[clamp(3rem,5.6vw,5.25rem)] font-black leading-[0.85] tracking-[-0.05em]'>
                                        <span aria-hidden='true' data-count>{result.value}</span>
                                        <span className='sr-only'>{result.value}</span>
                                    </p>
                                    <span aria-hidden='true' className='mt-5 block h-1 w-12 bg-coral' />
                                    <p className='mt-4 max-w-xs text-[15px] font-medium text-charcoal/85'>{result.label}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {related.length > 0 && (
                <section aria-labelledby='related-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <div data-reveal className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                            <div>
                                <SectionLabel>Related projects</SectionLabel>
                                <h2 id='related-title' className='mt-6 font-heading type-section'>
                                    Keep exploring.
                                </h2>
                            </div>
                            <CtaLink href='/work' variant='outline'>All projects</CtaLink>
                        </div>
                        <ProjectGrid projects={related} tone='light' className='mt-14' />
                    </div>
                </section>
            )}

            <FinalCta />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getSlugs('projects')

    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<CaseStudyPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const project = slug ? await getProject(slug) : null

    if (!project) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const projects = await getProjects()
    const related = projects.filter((item) => item.slug !== project.slug).slice(0, 2)
    const index = projects.findIndex((item) => item.slug === project.slug)

    return {
        props: {
            project,
            number: index >= 0 ? index + 1 : 1,
            total: Math.max(projects.length, 1),
            related
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default CaseStudyPage
